/**
 * Connector runs queued or in progress (Alembic 0206).
 *
 * The first live Archer run (P427) took seven minutes with nothing on
 * screen, and a second Run now press, queued behind it on the
 * connector lock, looked as though it had vanished. This says what is
 * running, since when, and how far it has got, and a run that has
 * stopped reporting is said to have stopped rather than shown as
 * running.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLiveConnectorRuns } from "shared/api/hooks/useConnectors";
import type { LiveConnectorRun } from "shared/api/types";
import { Figure } from "shared/components/Figure";
import { groupedCount, i18n } from "shared/i18n";

function elapsed(fromIso: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 1000));
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(m > 0 ? 2 : 1, "0");
  return m > 0
    ? i18n.t("screens.liveRuns.elapsedMinutes", { m, s })
    : i18n.t("screens.liveRuns.elapsedSeconds", { s });
}

function count(value: number): string {
  return String(groupedCount(value, i18n.language));
}

function LiveRunRow({ run, now }: { run: LiveConnectorRun; now: number }): JSX.Element {
  const { t } = useTranslation();
  const label = run.connector_label ?? run.connector_kind ?? run.connector_id;
  return (
    <li
      data-testid="live-run"
      data-phase={run.phase}
      data-stale={run.stale ? "true" : "false"}
      className="flex flex-wrap items-baseline gap-x-2 text-xs"
    >
      <span className="font-semibold text-slate-800">{label}</span>
      {run.phase === "queued" ? (
        <span className="text-slate-600">
          {t("screens.liveRuns.queued")} (<Figure>{elapsed(run.queued_at, now)}</Figure>
          )
        </span>
      ) : run.stale ? (
        <span data-testid="live-run-stale" className="text-amber-800">
          {t("screens.liveRuns.stale", { ago: elapsed(run.updated_at, now) })}
        </span>
      ) : (
        <span className="text-slate-700">
          {t("screens.liveRuns.running")}{" "}
          <Figure>{elapsed(run.started_at ?? run.queued_at, now)}</Figure>
        </span>
      )}
      {run.phase === "running" && (
        <span data-testid="live-run-counts" className="text-slate-600">
          {t("screens.liveRuns.counts", {
            batches: count(run.batches),
            inserted: count(run.row_inserted),
            updated: count(run.row_updated),
            skipped: count(run.row_skipped),
          })}
        </span>
      )}
      <span className="text-[10px] text-slate-500">{run.trigger_kind}</span>
    </li>
  );
}

export function LiveRunsPanel({
  connectorId,
  showWhenIdle = false,
}: {
  /** Only this connector's runs (the setup flow). */
  connectorId?: string;
  /** Say "nothing running" rather than render nothing. */
  showWhenIdle?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  const live = useLiveConnectorRuns();
  const rows = (Array.isArray(live.data) ? live.data : []).filter(
    (r) => connectorId === undefined || r.connector_id === connectorId,
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (rows.length === 0) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [rows.length]);

  if (rows.length === 0) {
    return showWhenIdle ? (
      <p data-testid="live-runs-idle" className="text-xs text-slate-500">
        {t("screens.liveRuns.idle")}
      </p>
    ) : null;
  }
  return (
    <section
      data-testid="live-runs"
      aria-live="polite"
      className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-2"
    >
      <h2 className="mb-1 text-xs font-semibold text-slate-800">
        {t("screens.liveRuns.heading")}
      </h2>
      <ul className="space-y-1">
        {rows.map((run) => (
          <LiveRunRow key={run.id} run={run} now={now} />
        ))}
      </ul>
    </section>
  );
}
